import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import AuthService from "./service/AuthService";
import InputModule from "./FormElements/InputModule";
import Button from "./ui/Button";

import { Fieldset, Form, Title } from "./SignupStyles";
import ErrorMessage from "./FormElements/ErrorMessage";
import ConfigService from "@/components/service/ConfigService.js";
import { useCookies } from "react-cookie";

function Text({ children }) {
  return <p className="mb-7 text-sm">{children}</p>;
}

const ForgotPassword = () => {
  const intl = useIntl();
  const principal = useSelector(state => state.authentication.principal);

  const configService = ConfigService.getInstance();
  const authService = AuthService.getInstance();

  const [email, setEmail] = useState(principal?.email ?? "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resetComplete, setResetComplete] = useState(false);
  const [html5error, setHtml5error] = useState({});

  const [cookies] = useCookies(['XSRF-TOKEN']);

  // The principal arrives in the background after mount. Prefill from it only
  // if the user hasn't started typing, so a late response doesn't clobber
  // their input.
  useEffect(() => {
    if (principal?.email) {
      setEmail((current) => current || principal.email);
    }
  }, [principal]);

  const handleInputChange = (event) => {
    const target = event.target;
    const inputName = target.name;
    const inputValue = target.value;

    target.setCustomValidity("");
    setHtml5error((prevState) => ({
      ...prevState,
      [inputName]: undefined,
    }));
    setEmail(inputValue);
  };

  const handleSubmit = (event) => {
    setLoading(true);
    setError(null);
    setHtml5error({});

    event.preventDefault();

    if (validate(event)) {
      authService
        .resetPassword(email, {token: cookies['XSRF-TOKEN'], headerName: "X-XSRF-TOKEN" })
        .then((_) => {
          setResetComplete(true);
        })
        .catch((error) => {
          setError(error);
          setLoading(false);
        })
        .finally((_) => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  const checkIsValid = (e) => {
    if (e.target.checkValidity() === false) {
      e.target.parentNode.classList.add("was-validated");
    }
  };

  const validate = (event) => {
    return event.target.checkValidity();
  };

  return (
    <div className="600:w-[360px] mx-auto mt-12 flex flex-col pb-12">
      {resetComplete ? (
        <>
          <Title>
            <FormattedMessage id="template.thankYou" defaultMessage="Thank you" />
          </Title>
          <Text>
            <FormattedMessage
              id="auth.forgotSentBody"
              defaultMessage="If there's a NewsGuard account associated with this email address, we'll email you password reset instructions. If you don't receive the email, please check your email's spam folder or <retry>try again</retry>."
              values={{
                retry: (chunks) => (
                  <Link to="/forgotPassword" onClick={() => setResetComplete(false)}>
                    {chunks}
                  </Link>
                ),
              }}
            />
          </Text>
          <Text>
            <FormattedMessage
              id="auth.forgotSentHint"
              defaultMessage="Make sure you enter the email address associated with your NewsGuard account."
            />
          </Text>
        </>
      ) : (
        <>
          <Title>
            <FormattedMessage
              id="template.forgot_password"
              defaultMessage="Forgot Password?"
            />
          </Title>

          <Form onSubmit={handleSubmit} noValidate>
            <Text>
              <FormattedMessage
                id="auth.forgotInstructions"
                defaultMessage="Submit the email associated with your NewsGuard account below and we'll send you instructions on how to reset your password."
              />
            </Text>

            <Fieldset disabled={loading} aria-busy={loading}>
              <InputModule
                type="email"
                identifier="email"
                autoComplete="email"
                placeholder={intl.formatMessage({
                  id: "template.email",
                  defaultMessage: "Email",
                })}
                value={email}
                onChange={handleInputChange}
                onBlur={checkIsValid}
                onInvalid={() => {
                  let email = {
                    id: "template.email_invalid_message",
                    defaultMessage: "Please enter a correct email address",
                    name: "email",
                    html: true,
                  };
                  setHtml5error((prevState) => ({
                    ...prevState,
                    email,
                  }));
                }}
                required
                error={html5error?.email}
              />

              {error && <ErrorMessage error={error} />}

              <Button type="submit" isLoading={loading} className="w-full">
                <FormattedMessage id="template.submit" defaultMessage="Submit" />
              </Button>
            </Fieldset>
          </Form>
        </>
      )}
      <Link
        to="/"
        onClick={() => window.location.href = configService.formulateContextPath('/signout')}
        className="mt-5 self-center text-sm"
      >
        {" "}
        <FormattedMessage id="auth.home" defaultMessage="Home" />
      </Link>
    </div>
  );
};

export default ForgotPassword;
